import { Cents, formatMoney, toMoney } from '@betterment/core';
import { Box, Icon, Tag } from '@betterment/samba-react';
import { Button, Flex, Slot, SlotProps, Text, TextProps, getSlots, theme } from '@betterment/style-closet-react';
import { formatDistanceToNow } from 'date-fns';
import { pick as _pick } from 'es-toolkit/compat';

import { AccountIcon } from '../../Icon/AccountIcon';
import { BankAccount, BettermentAccount, BettermentAccountType } from '../data';
import { isBettermentChecking } from '../models/bank-account';

export function determineAccountValues(account: BankAccount | BettermentAccount): {
  id: number;
  displayName: string;
  balanceCents?: Cents;
  type: BettermentAccountType | BankAccount['type'];
} {
  switch (account.type) {
    case 'bank_account':
      return _pick(account, ['id', 'displayName', 'balanceCents', 'type']);
    case 'betterment_checking':
      return {
        id: account.checkingAccount?.id || 0,
        displayName: account.name,
        balanceCents: account.displayBalanceCents,
        type: account.type,
      };
    case 'sub_account':
      return {
        id: account.investingAccount?.id,
        displayName: account.name,
        balanceCents: account.displayBalanceCents,
        type: account.type,
      };
    default:
      return null as never;
  }
}

const getColor = (color: keyof typeof theme.colors) => theme.colors[color];

const EmptyIcon: React.FC<React.PropsWithChildren<{ color: keyof typeof theme.colors; size?: number }>> = ({
  color,
  size = 36,
}) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 8C0 3.58172 3.58172 0 8 0H28C32.4183 0 36 3.58172 36 8V28C36 32.4183 32.4183 36 28 36H8C3.58172 36 0 32.4183 0 28V8Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.4387 35.8477L6.82684 33.8858C7.20466 33.9605 7.59673 34 8 34H9.66667V36H8C7.46572 36 6.94367 35.9476 6.4387 35.8477ZM26.3333 36V34H28C28.4033 34 28.7953 33.9605 29.1732 33.8858L29.5613 35.8477C29.0563 35.9476 28.5343 36 28 36H26.3333ZM32.4449 34.6525L31.3323 32.9906C31.9875 32.552 32.552 31.9875 32.9906 31.3323L34.6525 32.4449C34.0685 33.3173 33.3173 34.0685 32.4449 34.6525ZM36 9.66667H34V8C34 7.59673 33.9605 7.20466 33.8858 6.82684L35.8477 6.4387C35.9476 6.94367 36 7.46572 36 8V9.66667ZM34.6525 3.55508L32.9906 4.66771C32.552 4.01254 31.9875 3.44804 31.3323 3.00941L32.4449 1.34748C33.3173 1.93154 34.0685 2.68266 34.6525 3.55508ZM9.66667 0H8C7.46572 0 6.94367 0.0523755 6.4387 0.152272L6.82684 2.11425C7.20466 2.0395 7.59673 2 8 2H9.66667V0ZM3.55508 1.34748L4.66771 3.00941C4.01254 3.44804 3.44804 4.01254 3.00941 4.66771L1.34748 3.55507C1.93154 2.68266 2.68266 1.93154 3.55508 1.34748ZM0 26.3333H2V28C2 28.4033 2.0395 28.7953 2.11425 29.1732L0.152272 29.5613C0.0523756 29.0563 0 28.5343 0 28V26.3333ZM3.55507 34.6525L4.66771 32.9906C4.01254 32.552 3.44804 31.9875 3.00941 31.3323L1.34748 32.4449C1.93154 33.3173 2.68266 34.0685 3.55507 34.6525ZM0 23H2V19.6667H0V23ZM0 16.3333H2V13H0V16.3333ZM0 9.66667H2V8C2 7.59673 2.0395 7.20466 2.11425 6.82684L0.152272 6.4387C0.0523756 6.94367 0 7.46572 0 8V9.66667ZM13 0V2H16.3333V0H13ZM19.6667 0V2H23V0H19.6667ZM26.3333 0V2H28C28.4033 2 28.7953 2.0395 29.1732 2.11425L29.5613 0.152272C29.0563 0.0523756 28.5343 0 28 0H26.3333ZM36 13H34V16.3333H36V13ZM36 19.6667H34V23H36V19.6667ZM36 26.3333H34V28C34 28.4033 33.9605 28.7953 33.8858 29.1732L35.8477 29.5613C35.9476 29.0563 36 28.5343 36 28V26.3333ZM23 36V34H19.6667V36H23ZM16.3333 36V34H13V36H16.3333Z"
      fill={getColor(color)}
    />
  </svg>
);

function accountIsLocked(account: BankAccount | BettermentAccount) {
  switch (account.type) {
    case 'betterment_checking':
      return account.checkingAccount?.locked || false;
    case 'bank_account':
      return account?.isLocked || false;
    default:
      return false;
  }
}

const bankAccountPendingManualVerification = (account: BankAccount | BettermentAccount) => {
  return account.type === 'bank_account' && account.isPendingManualLink;
};

const shouldShowInstantTag = (account: BettermentAccount | BankAccount) => {
  if (account.type === 'sub_account') {
    return account.isInstantEligible;
  }
  return false;
};

interface AccountOptionProps extends TextProps {
  account: BankAccount | BettermentAccount;
  shouldShowInstantTag?: boolean;
  showBalance?: boolean;
  showAccountLocked?: boolean;
  showAccountPendingManualVerification?: boolean;
  iconSize?: number;
  balanceAlign?: 'left' | 'center' | 'right';
}

export const AccountOption: React.FC<React.PropsWithChildren<AccountOptionProps>> & {
  Slot: React.FC<React.PropsWithChildren<SlotProps>>;
} = ({
  account,
  children,
  showBalance = true,
  showAccountLocked = true,
  showAccountPendingManualVerification = true,
  balanceAlign = 'left',
  ...textProps
}) => {
  const { displayName, balanceCents } = determineAccountValues(account);
  const formattedBalance = typeof balanceCents === 'number' ? formatMoney(toMoney(balanceCents)) : undefined;
  showBalance = showBalance && !!formattedBalance;
  const lastUpdated =
    account.type === 'bank_account' &&
    !isBettermentChecking(account) &&
    account.balanceUpdatedAt &&
    formatDistanceToNow(new Date(account.balanceUpdatedAt));
  const [content] = getSlots(['content'], children);
  const isLocked = accountIsLocked(account);
  const isBankAccountPendingManualVerification = bankAccountPendingManualVerification(account);

  return (
    <Box css={{ display: 'flex', alignItems: 'center', width: '100%', gap: 'space.100' }}>
      <AccountIcon asset={account.icon} name="account icon" />
      <Flex flexDirection="row" justifyContent="space-between" width="100%">
        <Flex flexDirection="column">
          <Text fontSize="2" lineHeight="1" textAlign="left" {...textProps}>
            {displayName}
          </Text>
          {showAccountPendingManualVerification && isBankAccountPendingManualVerification && (
            <Flex alignItems="center" mt="1">
              <Flex mr="1">
                <Icon name="ExclamationCircle" size="xsmall" color="icon.weak" />
              </Flex>
              <Text fontSize="1" color="bmtGrey50">
                Needs verification
              </Text>
            </Flex>
          )}
          {showBalance && !isBankAccountPendingManualVerification && (
            <Flex>
              <Text color="bmtGrey50" fontSize="1" lineHeight="1" textAlign={balanceAlign}>
                {formattedBalance} {lastUpdated && <>&middot; {lastUpdated} ago</>}
              </Text>
              {content}
            </Flex>
          )}
        </Flex>
        {showAccountLocked && isLocked && (
          <Flex alignItems="center" mt="1">
            <Flex mr="1">
              <Icon name="Lock" size="small" color="icon.critical" />
            </Flex>
            <Text fontSize="1" color="bmtOrange90">
              Account locked
            </Text>
          </Flex>
        )}
        {shouldShowInstantTag(account) && (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Tag accent="teal">
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Icon name="Lightning" size="xxsmall" color="icon.accent.teal.strong" />
                <Text fontSize="1" lineHeight="1">
                  Instant
                </Text>
              </Box>
            </Tag>
          </Box>
        )}
      </Flex>
    </Box>
  );
};

AccountOption.Slot = Slot;

interface EmptyAccountOptionProps {
  ariaLabel: string;
  onClick: () => void;
  disabled?: boolean;
}

export const EmptyAccountOption: React.FC<React.PropsWithChildren<EmptyAccountOptionProps>> = ({
  onClick,
  ariaLabel,
  disabled = false,
}) => {
  const color: keyof typeof theme.colors = disabled ? 'bmtGrey60' : 'bmtBlue';

  return (
    <Button aria-label={ariaLabel} variant="tertiary" type="button" onClick={() => onClick()} disabled={disabled}>
      <Flex alignItems="center">
        <Flex.Item>
          <Flex mr="3">
            <EmptyIcon color={color} />
          </Flex>
        </Flex.Item>
        <Flex.Item>
          <Text fontSize="2" color={color} lineHeight="1">
            Select account
          </Text>
        </Flex.Item>
      </Flex>
    </Button>
  );
};

export const LinkBankAccountOption: React.FC<
  React.PropsWithChildren<Pick<EmptyAccountOptionProps, 'onClick' | 'ariaLabel'>>
> = ({ onClick, ariaLabel }) => {
  return (
    <Button aria-label={ariaLabel} variant="tertiary" type="button" onClick={() => onClick()}>
      <Flex alignItems="center">
        <Flex.Item>
          <Flex mr="3">
            <EmptyIcon color="bmtBlue" />
          </Flex>
        </Flex.Item>
        <Flex.Item>Connect a bank account</Flex.Item>
      </Flex>
    </Button>
  );
};
